/**
 * useMcpAppContent — Fetches MCP App HTML content with SWR caching.
 */
import { AppApi } from '@/customApiClients/appApiClient/Api';
import useSWR, { mutate, preload } from 'swr';

export interface UseMcpAppContentOptions {
    /** The ui:// resource URI to fetch */
    resourceUri: string;
    /** Direct server URL — bypasses singleton, works from chat history */
    serverUrl?: string;
    /** Server name (optional - backend will use first available if not specified) */
    serverName?: string;
    /** Whether to auto-fetch on mount */
    enabled?: boolean;
}

export interface UseMcpAppContentReturn {
    /** Fetched HTML content (may be stale while revalidating) */
    html: string | null;
    /** True only when there is no cached value and the first fetch is in-flight */
    isLoading: boolean;
    /** True when a background revalidation is running against a stale entry */
    isRevalidating: boolean;
    /** Error message — only set when the most recent attempt failed */
    error: string | null;
    /** Force a fresh fetch, bypassing the cache */
    refetch: () => Promise<void>;
}

const appApi = new AppApi({
    baseUrl: window.location.origin,
    baseApiParams: { credentials: 'include' }
});

async function fetchMcpHtml([_prefix, resourceUri, serverUrl, serverName]: [
    string,
    string,
    string | undefined,
    string | undefined
]): Promise<string> {
    const response = await appApi.mcp.fetchMcpResource({
        resource_uri: resourceUri,
        server_name: serverName,
        server_url: serverUrl
    });

    const content = response.data?.content;
    if (!content) throw new Error('Resource returned empty content');
    return content;
}

export function prefetchMcpAppContent(
    resourceUri: string,
    serverUrl?: string
): Promise<string> {
    return preload(
        ['mcp-app-html', resourceUri, serverUrl, undefined] as const,
        fetchMcpHtml
    );
}

export function useMcpAppContent({
    resourceUri,
    serverUrl,
    serverName,
    enabled = true
}: UseMcpAppContentOptions): UseMcpAppContentReturn {
    // SWR key — null disables fetching when enabled=false or resourceUri is empty.
    // An array key lets SWR distinguish calls with different serverUrl/serverName.
    const swrKey =
        enabled && resourceUri
            ? (['mcp-app-html', resourceUri, serverUrl, serverName] as const)
            : null;

    const { data, isLoading, isValidating, error } = useSWR(
        swrKey,
        fetchMcpHtml,
        {
            // Keep stale data visible while revalidating in background.
            keepPreviousData: true,
            // Revalidate after 5 minutes (matches our previous STALE_AFTER_MS).
            dedupingInterval: 5 * 60 * 1000,
            // Don't spam the server on every focus/reconnect for chart data.
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    );

    const refetch = async () => {
        if (swrKey) {
            await mutate(swrKey);
        }
    };

    return {
        html: data ?? null,
        // isLoading = true only when there's no cached data AND a fetch is in-flight.
        isLoading: isLoading,
        // isValidating = true whenever any fetch is in-flight (including revalidation).
        // isRevalidating is true only during background refresh (we have data already).
        isRevalidating: isValidating && !isLoading,
        error: error?.message ?? null,
        refetch
    };
}
